import { toast } from "sonner";

// Define the API base URL
export const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Define custom event for unauthorized access
export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

interface RequestOptions extends RequestInit {
    // Option to skip error notification
    skipErrorToast?: boolean;
    // Option to skip automatic 401 handling
    skipAuthHandler?: boolean;
}

export class ApiError extends Error {
    status: number;
    data: any;

    constructor(message: string, status: number, data: any) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.data = data;
    }
}

/**
 * Universal helper for making API requests
 * automatically handles auth tokens, JSON parsing, and error logging.
 */
async function request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { skipErrorToast = false, skipAuthHandler = false, ...fetchOptions } = options;

    let url = endpoint;

    // If endpoint doesn't start with http, prepend API_BASE
    if (!endpoint.startsWith("http")) {
        const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
        url = `${API_BASE}${cleanEndpoint}`;
    }

    // Get token from storage
    const token = localStorage.getItem("token");

    const isFormData = fetchOptions.body instanceof FormData;

    // Set default headers
    const headers: HeadersInit = {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...fetchOptions.headers,
    };

    try {
        const response = await fetch(url, {
            ...fetchOptions,
            headers,
        });

        // Handle 401 Unauthorized
        if (response.status === 401 && !skipAuthHandler) {
            // Dispatch custom event for AuthContext to handle (e.g., logout/redirect)
            window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));

            // We can also throw an error here, or let the AuthContext handle the redirect immediately.
            // Throwing ensures the calling code stops execution.
            throw new ApiError("Unauthorized", 401, { message: "Session expired or invalid" });
        }

        // Try to parse JSON
        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            try {
                data = await response.json();
            } catch (e) {
                data = null;
            }
        } else {
            data = await response.text();
        }

        if (!response.ok) {
            // Extract error message
            let errorMessage = "An unexpected error occurred";
            if (typeof data === 'object' && data !== null && data.message) {
                errorMessage = data.message;
            } else if (typeof data === 'object' && data !== null && Array.isArray(data.errors) && data.errors.length > 0) {
                // Express-validator style errors: { errors: [{ msg, path, param }, ...] }
                const messages = data.errors.map((e: any) => {
                    const field = e.path || e.param || 'field';
                    const msg = e.msg || e.message || 'Invalid value';
                    return `${field}: ${msg}`;
                });
                errorMessage = messages.join('; ');
            } else if (typeof data === 'string' && data.length > 0) {
                // If the text response is short enough to be a message
                errorMessage = data.length < 100 ? data : response.statusText;
            } else {
                errorMessage = response.statusText;
            }

            throw new ApiError(errorMessage, response.status, data);
        }

        return data as T;
    } catch (error: any) {
        const shouldShowToast = !skipErrorToast && error.name !== "AbortError";

        if (shouldShowToast) {
            // Show toast for errors except 401 (AuthContext handles that)
            if (error.status !== 401 && error.status !== 403) {
                toast.error(error.message || "Request failed");
            }
        }

        // Always throw to ensure calling code enters its catch block
        throw error;
    }
}

// Convenience methods
export const api = {
    get: <T = any>(endpoint: string, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: "GET" }),

    post: <T = any>(endpoint: string, body: any, options?: RequestOptions) => {
        const isFormData = body instanceof FormData;
        return request<T>(endpoint, {
            ...options,
            method: "POST",
            body: isFormData ? body : JSON.stringify(body)
        });
    },

    put: <T = any>(endpoint: string, body: any, options?: RequestOptions) => {
        const isFormData = body instanceof FormData;
        return request<T>(endpoint, {
            ...options,
            method: "PUT",
            body: isFormData ? body : JSON.stringify(body)
        });
    },

    patch: <T = any>(endpoint: string, body: any, options?: RequestOptions) => {
        const isFormData = body instanceof FormData;
        return request<T>(endpoint, {
            ...options,
            method: "PATCH",
            body: isFormData ? body : JSON.stringify(body)
        });
    },

    delete: <T = any>(endpoint: string, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: "DELETE" }),

    // Allow direct access to the request function
    request
};

export default api;
